import emailjs from '@emailjs/browser';

interface SendSurveyEmailParams {
  to: string;
  recipientName: string;
  surveyTitle: string;
  surveyLink: string;
}

interface SendWelcomeEmailParams {
  adminName: string;
  adminEmail: string;
  orgName: string;
  registerLink: string;
}

export async function sendSurveyEmail(params: SendSurveyEmailParams) {
  if (!params.to) {
    throw new Error('Recipient email address is required');
  }

  try {
    const response = await emailjs.send(
      import.meta.env.VITE_EMAILJS_SERVICE_ID,
      import.meta.env.VITE_EMAILJS_TEMPLATE_ID,
      {
        to_email: params.to,
        to_name: params.recipientName,
        survey_title: params.surveyTitle,
        survey_link: params.surveyLink,
        company_name: 'AI Prime Consulting',
        support_email: 'support@aiprimesurveys.com'
      }
    );

    if (!response.status || response.status !== 200) {
      throw new Error('Failed to send email');
    }

    return { success: true, data: response };
  } catch (error) {
    console.error('Error sending email:', error);
    throw error;
  }
}

interface SendCampaignEmailParams {
  to: string;
  recipientName: string;
  campaignName: string;
  surveyTitles: string[];
  campaignLink: string;
}

export async function sendCampaignEmail(params: SendCampaignEmailParams) {
  if (!params.to) {
    throw new Error('Recipient email address is required');
  }

  try {
    const response = await emailjs.send(
      import.meta.env.VITE_EMAILJS_SERVICE_ID,
      import.meta.env.VITE_EMAILJS_CAMPAIGN_TEMPLATE_ID,
      {
        to_email: params.to,
        to_name: params.recipientName,
        campaign_name: params.campaignName,
        survey_list: params.surveyTitles.map((t, i) => `${i + 1}. ${t}`).join('\n'),
        survey_count: params.surveyTitles.length.toString(),
        campaign_link: params.campaignLink,
        company_name: 'AI Prime Consulting',
        support_email: 'support@aiprimesurveys.com'
      }
    );

    if (!response.status || response.status !== 200) {
      throw new Error('Failed to send campaign email');
    }

    return { success: true, data: response };
  } catch (error) {
    throw error;
  }
}

export async function sendWelcomeEmail(params: SendWelcomeEmailParams) {
  // Validate required parameters
  if (!params.adminEmail) {
    throw new Error('Admin email address is required');
  }
  if (!params.adminName) {
    throw new Error('Admin name is required');
  }
  if (!params.orgName) {
    throw new Error('Organization name is required');
  }
  if (!params.registerLink) {
    throw new Error('Register link is required');
  }

  try {
    const response = await emailjs.send(
      import.meta.env.VITE_EMAILJS_SERVICE_ID,
      import.meta.env.VITE_EMAILJS_WELCOME_TEMPLATE_ID,
      {
        to_email: params.adminEmail, // Add recipient email to template variables
        admin_name: params.adminName,
        admin_email: params.adminEmail,
        org_name: params.orgName,
        register_link: params.registerLink
      }
    );

    if (!response.status || response.status !== 200) {
      throw new Error('Failed to send welcome email');
    }

    return { success: true, data: response };
  } catch (error) {
    console.error('Error sending welcome email:', error);
    throw error;
  }
}